import { useCallback, useRef } from "react";

import { createIdempotencyKey } from "../lib/api";

export interface SubmissionAttempt {
  scope: string;
  signature: string;
  key: string;
}

type FileDescriptor = Pick<File, "name" | "size" | "type" | "lastModified">;

export function createSubmissionSignature(payload: unknown, files: readonly FileDescriptor[] = []) {
  return JSON.stringify({
    payload,
    files: files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    })),
  });
}

export function resolveSubmissionAttempt(
  current: SubmissionAttempt | null,
  scope: string,
  signature: string,
  keyFactory: (scope: string) => string = createIdempotencyKey,
): SubmissionAttempt {
  if (current?.scope === scope && current.signature === signature) return current;
  return { scope, signature, key: keyFactory(scope) };
}

export function useSubmissionAttempt(scope: string) {
  const attemptRef = useRef<SubmissionAttempt | null>(null);

  const keyFor = useCallback((signature: string) => {
    const attempt = resolveSubmissionAttempt(attemptRef.current, scope, signature);
    attemptRef.current = attempt;
    return attempt.key;
  }, [scope]);

  const reset = useCallback((completedKey?: string) => {
    if (!completedKey || attemptRef.current?.key === completedKey) attemptRef.current = null;
  }, []);

  return { keyFor, reset };
}
